/**
 * DocumentsStep — Dokumenten-Upload-Schritt im Case Editor.
 * Stateless: bekommt Akte + onChange als Props.
 */
import React, { useState } from "react";
import { FileText, Upload, Loader2, Eye, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import AuthImg from "@/components/AuthImg";
import { StepHeader } from "@/components/case/_shared";


export default function DocumentsStep({ c, onChange }) {
  const { accessToken } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [preview, setPreview] = useState(null);

  const docs = c.documents || [];

  const isPdf = (d) => (d?.mime_type || d?.original_filename || "").toLowerCase().includes("pdf");
  const isImg = (d) => (d?.mime_type || "").startsWith("image") || /\.(png|jpe?g|webp|heic|heif)$/i.test(d?.original_filename || "");
  const fileUrl = (d) => d ? `/api/files/download/${encodeURI(d.storage_path)}?auth=${accessToken || ""}` : "#";

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    fd.append("doc_type", "other");
    setUploading(true);
    try {
      const { data } = await api.post(`/files/cases/${c.id}/documents`, fd, {});
      onChange([...(c.documents || []), ...data.documents]);
      toast.success(`${data.documents.length} Dokument(e) hochgeladen`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`„${d.original_filename}" wirklich löschen?`)) return;
    setDeleting(d.id);
    try {
      await api.delete(`/files/cases/${c.id}/documents/${d.id}`);
      onChange((c.documents || []).filter((x) => x.id !== d.id));
      toast.success("Dokument gelöscht");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(null);
    }
  };

  const openDoc = (d) => {
    if (isImg(d)) setPreview(d);
    else window.open(fileUrl(d), "_blank", "noopener");
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="w-full space-y-5" data-testid="step-documents">
      <StepHeader icon={FileText} title="Dokumente" subtitle="PDFs und Bilder zur Akte" />

      <label
        className="block border-2 border-dashed border-zinc-300 rounded-2xl p-10 text-center cursor-pointer hover:border-cyan-400 hover:bg-cyan-50/30 transition"
        data-testid="document-dropzone"
      >
        <input
          type="file"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid="document-file-input"
        />
        <div className="flex flex-col items-center gap-3">
          {uploading ? <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" /> : (
            <div className="h-12 w-12 rounded-2xl brand-gradient-soft flex items-center justify-center">
              <Upload className="h-5 w-5 text-cyan-700" />
            </div>
          )}
          <div>
            <div className="font-medium">Dokumente hochladen</div>
            <div className="text-sm text-zinc-500">PDF, Bilder, alle Dateitypen erlaubt</div>
          </div>
        </div>
      </label>

      {docs.length > 0 ? (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="documents-uploaded-list">
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50/50 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500">
              Hochgeladene Dokumente · {docs.length}
            </div>
            <div className="text-[11px] text-zinc-400">Klicken zum Öffnen</div>
          </div>
          <div className="divide-y divide-zinc-100">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors"
                data-testid={`doc-row-${d.id}`}
              >
                <button
                  type="button"
                  onClick={() => openDoc(d)}
                  className="relative h-12 w-12 rounded-xl overflow-hidden border border-zinc-200 bg-zinc-100 shrink-0 hover:ring-2 hover:ring-cyan-400 transition"
                  title="Öffnen"
                  data-testid={`doc-open-${d.id}`}
                >
                  {isImg(d) ? (
                    <AuthImg path={d.storage_path} token={accessToken} alt={d.original_filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-cyan-600">
                      <FileText className="h-4 w-4" strokeWidth={1.8} />
                      <span className="text-[7px] font-bold tracking-wider mt-0.5">
                        {isPdf(d) ? "PDF" : "FILE"}
                      </span>
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => openDoc(d)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="font-display font-semibold text-sm tracking-tight text-zinc-900 truncate">
                    {d.original_filename || "Unbekanntes Dokument"}
                  </div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1 bg-zinc-100 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                      {d.doc_type === "fahrzeugschein" ? "Fahrzeugschein"
                        : d.doc_type === "unfallbericht" ? "Unfallbericht"
                        : isPdf(d) ? "PDF"
                        : isImg(d) ? "Bild"
                        : (d.doc_type || "Datei")}
                    </span>
                    {d.size ? <span>{fmtSize(d.size)}</span> : null}
                    {d.uploaded_at ? <span>· {new Date(d.uploaded_at).toLocaleDateString("de-DE")}</span> : null}
                  </div>
                </button>

                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDoc(d)} title="Öffnen" data-testid={`doc-preview-${d.id}`}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <a href={fileUrl(d)} download={d.original_filename} target="_blank" rel="noreferrer"
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-zinc-100 text-zinc-600"
                    title="Download" data-testid={`doc-download-${d.id}`}>
                    <Download className="h-4 w-4" />
                  </a>
                  <Button
                    variant="ghost" size="icon"
                    className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    onClick={() => removeDoc(d)} disabled={deleting === d.id}
                    title="Löschen" data-testid={`doc-delete-${d.id}`}
                  >
                    {deleting === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-400" data-testid="documents-empty">
          Noch keine weiteren Dokumente hochgeladen.
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="document-preview-overlay">
          <div className="max-w-4xl w-full bg-white rounded-xl p-4 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
            <AuthImg path={preview.storage_path} token={accessToken} alt={preview.original_filename}
              className="max-h-[70vh] w-full object-contain bg-zinc-100 rounded-lg" />
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium truncate">{preview.original_filename}</div>
              <div className="flex items-center gap-2">
                <a href={fileUrl(preview)} download={preview.original_filename} target="_blank" rel="noreferrer"
                  className="text-sm text-cyan-700 hover:underline inline-flex items-center gap-1">
                  <Download className="h-4 w-4" /> Download
                </a>
                <Button variant="ghost" size="sm" onClick={() => setPreview(null)}>Schließen</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
